/**
 * Changes: what a folder on this machine and the project in Lattice would do
 * to each other.
 *
 * THE SHAPE OF THE SCREEN, AND WHY
 *   Two groups — Incoming and Outgoing — because those are the two directions
 *   work travels, and a person almost always wants one of them wholesale:
 *   "give me what everyone else did" or "send what I did". Those are Pull and
 *   Push, and they are one press each.
 *
 *   Conflicts and Blocked are shown only when they exist, and they are the
 *   exceptions that make the rest safe:
 *     - a CONFLICT is the one place a person has to think, so it defaults to
 *       neither choice, is drawn differently, and cannot be settled by Pull,
 *       by Push, or by "accept all" in another group;
 *     - a BLOCKED file is one a colleague has locked. We never push over it.
 *       Locks are what make this whole screen simple: lock a part before you
 *       edit it and nobody can change the remote copy underneath you, so
 *       pushing afterwards cannot conflict in the first place.
 *
 * WHAT THIS FILE DOES NOT DO
 *   It decides nothing. Every comparison comes from @lattice/core's
 *   `buildSyncPlan`, every byte is moved by the desktop shell, and the store
 *   in sync-folder.ts holds what the person chose. This is the drawing.
 */
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  AlertTriangle, ArrowDown, ArrowUp, CheckCircle2, FolderOpen, FolderPlus, FolderSync,
  Hand, Lock, RefreshCw, Unlink, X,
} from 'lucide-react'
import {
  basename, blockingLock, describeItem, pullOnly, pushOnly,
  type Path, type ProjectId, type Resolution, type SyncItem, type SyncPlan,
} from '@lattice/core'
import { useWorkspace, personOf, filesByProject, locksHeldNow } from '@/store/workspace'
import { useUi } from '@/store/ui'
import { api } from '@/store/api'
import {
  cancelRun, countsFor, describeChoice, groupPlan, linkProject, loadLink, planFor,
  rescan, resolutionOf, resolutionsFor, revealFolder, runSync, unlinkProject,
  useSyncFolder, type Progress, type RunReport,
} from '@/store/sync-folder'
import { canSyncFolders, isDesktop } from '@/hooks/useDesktop'
import { DownloadApp } from '@/components/DownloadApp'
import { FileIcon } from '@/components/FileIcon'
import { Avatar, Badge, Button, EmptyState, Spinner, Tooltip } from '@/components/ui/primitives'
import { formatBytes, formatExact, formatRelative, pluralise } from '@/lib/format'
import { cn } from '@/lib/utils'

/**
 * The Changes screen for one project.
 * SyncView : {projectId} -> Element
 */
export function SyncView({ projectId }: { projectId: ProjectId }) {
  // Whether a filesystem exists is fixed for the lifetime of the window: the
  // shell injects its bridge before any of this runs.
  if (!canSyncFolders()) return <NoFilesystem />
  return <LinkedProject projectId={projectId} />
}

/* ------------------------------------------------------------------ */
/* No filesystem to reach                                              */
/* ------------------------------------------------------------------ */

/**
 * A browser tab cannot mirror a folder, and pretending otherwise with a button
 * that fails is worse than saying so. In a desktop build that predates folder
 * sync the answer is different — update it — so the two are told apart.
 */
function NoFilesystem() {
  return (
    <div className="h-full overflow-y-auto">
      <div className="mx-auto max-w-2xl space-y-4 px-4 py-8 fade-in">
        <header className="space-y-1">
          <h2 className="flex items-center gap-2 text-lg font-semibold tracking-tight">
            <FolderSync className="h-4.5 w-4.5 text-[var(--fg-muted)]" strokeWidth={1.5} />
            Folder sync
          </h2>
          <p className="text-sm text-[var(--fg-muted)]">
            {isDesktop()
              ? 'This desktop build does not have folder sync yet. Update it and the Changes screen will appear here.'
              : 'Keep a project mirrored in a folder on your machine, and pull or push changes when you want to. A browser tab has no filesystem to mirror into, so this part lives in the desktop app.'}
          </p>
        </header>

        <div className="rounded-[var(--radius-lg)] border border-[var(--border)] bg-inset p-3 text-sm text-[var(--fg-muted)]">
          <p className="font-medium text-[var(--fg)]">Everything else works here</p>
          <p className="mt-0.5 text-[var(--fg-subtle)]">
            Browsing, previews, uploads, downloads, locks and comments are all in the browser. The desktop
            app adds the folder on disk — and notifications.
          </p>
        </div>

        <DownloadApp force={isDesktop()} />
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* The screen proper                                                   */
/* ------------------------------------------------------------------ */

function LinkedProject({ projectId }: { projectId: ProjectId }) {
  const link = useSyncFolder(state => state.links[projectId])
  const scan = useSyncFolder(state => state.scan)
  const overrides = useSyncFolder(state => state.overrides)
  const progress = useSyncFolder(state => state.progress)
  const error = useSyncFolder(state => state.error)
  const report = useSyncFolder(state => state.report)
  const chooseMany = useSyncFolder(state => state.chooseMany)
  const choose = useSyncFolder(state => state.choose)

  const changes = useWorkspace(state => state.changes)
  const lockLog = useWorkspace(state => state.locks)
  const me = useWorkspace(state => state.user)

  /* The link lives on disk rather than in this window, so find it again on
     open — and scan once, because a screen about differences that has not
     looked at the folder has nothing to say. */
  const autoScanned = useRef<string | undefined>(undefined)
  useEffect(() => {
    void loadLink(projectId).then(() => {
      const state = useSyncFolder.getState()
      if (!state.links[projectId]) return
      if (state.scan?.projectId === projectId || autoScanned.current === projectId) return
      autoScanned.current = projectId
      void rescan(projectId)
    })
  }, [projectId])

  const plan = useMemo((): SyncPlan | undefined => {
    if (!link || scan?.projectId !== projectId) return undefined
    return planFor({
      projectId,
      base: link.base,
      local: scan.files,
      files: filesByProject(changes).get(projectId) ?? new Map(),
      locks: locksHeldNow(lockLog),
      ...(me ? { me: me.id } : {}),
    })
  }, [link, scan, projectId, changes, lockLog, me])

  const resolutions = useMemo(
    () => (plan ? resolutionsFor(plan, overrides) : new Map<Path, Resolution>()),
    [plan, overrides],
  )
  const counts = useMemo(
    () => (plan ? countsFor(plan, resolutions) : { transfers: 0, bytes: 0, records: 0 }),
    [plan, resolutions],
  )
  const groups = useMemo(() => (plan ? groupPlan(plan) : undefined), [plan])

  const busy = progress.phase !== 'idle'
  const run = useCallback((kind: 'pull' | 'push' | 'sync') => {
    if (!plan) return
    const chosen = kind === 'pull' ? pullOnly(plan) : kind === 'push' ? pushOnly(plan) : resolutions
    void runSync(projectId, plan, chosen, kind)
  }, [plan, resolutions, projectId])

  if (!link) return <NotLinked projectId={projectId} busy={busy} />

  return (
    <div className="flex h-full flex-col">
      <FolderHeader
        dir={link.dir}
        lastSyncedAt={link.lastSyncedAt}
        scannedAt={scan?.projectId === projectId ? scan.at : undefined}
        busy={busy}
        projectId={projectId}
      />

      {plan && !plan.clean && (
        <Actions
          plan={plan}
          counts={counts}
          undecided={groups ? groups.conflicts.filter(item => !overrides.has(item.path)).length : 0}
          busy={busy}
          onRun={run}
        />
      )}

      {busy && <ProgressStrip progress={progress} />}
      {scan?.projectId === projectId && scan.skippedHashes > 0 && (
        <Unread count={scan.skippedHashes} />
      )}
      {error && <Problem message={error} />}
      {report && !busy && <Summary report={report} />}

      {/* Counts, for anybody listening rather than looking. */}
      <p aria-live="polite" className="sr-only">
        {plan
          ? `${plan.incoming} incoming, ${plan.outgoing} outgoing, ${pluralise(plan.conflicts, 'conflict')}, ${pluralise(plan.blocked, 'file')} blocked by a lock.`
          : 'Scanning the folder.'}
      </p>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {!plan ? (
          <EmptyState
            icon={FolderSync}
            title={busy ? 'Reading your folder…' : 'Nothing scanned yet'}
            hint={busy
              ? 'Every file is hashed, so Lattice can tell a real change from a file that was merely touched.'
              : 'Press Rescan to compare this folder with Lattice.'}
            action={busy ? undefined : <Button onClick={() => void rescan(projectId)}>Rescan</Button>}
          />
        ) : plan.clean ? (
          <EmptyState
            icon={CheckCircle2}
            title="Your folder matches Lattice"
            hint={scan
              ? `${pluralise(scan.files.size, 'file')} checked ${formatRelative(scan.at)}${scan.skippedHashes > 0 ? ` · ${scan.skippedHashes} could not be read` : ''}.`
              : undefined}
            action={<Button onClick={() => void rescan(projectId)} disabled={busy}>Rescan</Button>}
          />
        ) : groups ? (
          <div className="pb-8">
            {groups.conflicts.length > 0 && (
              <Group
                id="conflicts"
                icon={AlertTriangle}
                tone="text-[var(--modified)]"
                title="Conflicts"
                hint="Changed in both places. Nothing happens to these until you choose one — Pull and Push both leave them alone."
                items={groups.conflicts}
                overrides={overrides}
                onChoose={choose}
                actions={[
                  {
                    label: 'Take Lattice for all',
                    hint: 'Answer every conflict with the version in Lattice',
                    onClick: () => chooseMany(groups.conflicts.map(i => i.path), 'accept-remote'),
                  },
                  {
                    label: 'Take mine for all',
                    hint: 'Answer every conflict with the version in your folder',
                    onClick: () => chooseMany(groups.conflicts.map(i => i.path), 'keep-local'),
                  },
                ]}
                projectId={projectId}
              />
            )}

            {groups.incoming.length > 0 && (
              <Group
                id="incoming"
                icon={ArrowDown}
                tone="text-[var(--added)]"
                title="Incoming"
                hint="Work done in Lattice that your folder does not have yet."
                items={groups.incoming}
                overrides={overrides}
                onChoose={choose}
                actions={[
                  {
                    label: 'Accept all',
                    hint: 'Bring every one of these into your folder',
                    onClick: () => chooseMany(groups.incoming.map(i => i.path), 'accept-remote'),
                  },
                  // Deliberately "skip", not "keep local". On a file that
                  // exists ONLY in Lattice, keeping local means deleting it
                  // there, for everybody — which is not what anybody reads
                  // into a bulk button next to "Accept all". The per-file
                  // choice still offers it, and says exactly that.
                  {
                    label: 'Leave all',
                    hint: 'Bring none of them in, and change nothing in Lattice',
                    onClick: () => chooseMany(groups.incoming.map(i => i.path), 'skip'),
                  },
                ]}
                projectId={projectId}
              />
            )}

            {groups.outgoing.length > 0 && (
              <Group
                id="outgoing"
                icon={ArrowUp}
                tone="text-[var(--accent)]"
                title="Outgoing"
                hint="Work on this machine that Lattice does not have yet."
                items={groups.outgoing}
                overrides={overrides}
                onChoose={choose}
                actions={[
                  {
                    label: 'Send all',
                    hint: 'Upload every one of these to Lattice',
                    onClick: () => chooseMany(groups.outgoing.map(i => i.path), 'keep-local'),
                  },
                  {
                    label: 'Discard all local',
                    hint: 'Throw away these local changes — a file Lattice does not have is DELETED from your folder',
                    onClick: () => chooseMany(groups.outgoing.map(i => i.path), 'accept-remote'),
                  },
                ]}
                projectId={projectId}
              />
            )}

            {groups.blocked.length > 0 && (
              <BlockedGroup items={groups.blocked} projectId={projectId} />
            )}

            {groups.converged.length > 0 && (
              <p className="px-3 py-2 text-xs text-[var(--fg-subtle)]">
                {pluralise(groups.converged.length, 'file')} already identical on both sides — the next
                sync records that and moves nothing.
              </p>
            )}
          </div>
        ) : null}
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* The link                                                            */
/* ------------------------------------------------------------------ */

/** What linking is, before anybody has done it. */
function NotLinked({ projectId, busy }: { projectId: ProjectId; busy: boolean }) {
  const project = useWorkspace(state => state.projects.find(p => p.id === projectId))

  return (
    <div className="h-full overflow-y-auto">
      <div className="mx-auto max-w-xl space-y-3 px-4 py-10 fade-in">
        <FolderPlus className="h-7 w-7 text-[var(--fg-subtle)]" strokeWidth={1.25} />
        <h2 className="text-lg font-semibold tracking-tight">
          Mirror {project?.name ?? 'this project'} into a folder
        </h2>
        <p className="text-sm text-[var(--fg-muted)] leading-relaxed">
          Pick a folder on this machine and Lattice will compare it with the project whenever you ask.
          You then pull what colleagues have done, push what you have done, and decide file by file
          when the two disagree.
        </p>
        <ul className="space-y-1.5 text-sm text-[var(--fg-subtle)]">
          <li>Linking moves nothing. It records where the folder is and looks at what is in it.</li>
          <li>Work in the folder with SolidWorks, or anything else, exactly as you do now.</li>
          <li>Lock a file in Lattice before you edit it and nobody can change the shared copy
            underneath you — which is what keeps pushing simple.</li>
        </ul>
        <div className="flex items-center gap-2 pt-1">
          <Button variant="accent" size="lg" disabled={busy} onClick={() => void linkProject(projectId)}>
            {busy ? <Spinner size={14} /> : <FolderOpen className="h-4 w-4" strokeWidth={1.75} />}
            Choose folder…
          </Button>
        </div>
      </div>
    </div>
  )
}

/** Where the folder is, when it was last synced, and how to leave. */
function FolderHeader({ dir, lastSyncedAt, scannedAt, busy, projectId }: {
  dir: string
  lastSyncedAt?: string
  scannedAt?: string
  busy: boolean
  projectId: ProjectId
}) {
  const [confirming, setConfirming] = useState(false)

  return (
    <header className="flex h-9 shrink-0 items-center gap-2 border-b border-[var(--border)] bg-app px-3">
      <FolderSync className="h-3.5 w-3.5 shrink-0 text-[var(--fg-subtle)]" strokeWidth={1.75} />
      <Tooltip label="Show this folder on your machine">
        <button
          onClick={() => void revealFolder(projectId)}
          className="snap min-w-0 truncate font-mono text-xs text-[var(--fg-muted)] hover:text-[var(--accent)]"
        >
          {dir}
        </button>
      </Tooltip>

      <span className="hidden shrink-0 text-xs text-[var(--fg-subtle)] sm:inline">
        {lastSyncedAt ? (
          <Tooltip label={formatExact(lastSyncedAt)}>
            <span className="cursor-help underline decoration-dotted underline-offset-2">
              synced {formatRelative(lastSyncedAt)}
            </span>
          </Tooltip>
        ) : scannedAt ? (
          <span>scanned {formatRelative(scannedAt)}</span>
        ) : (
          <span>not synced yet</span>
        )}
      </span>

      <div className="flex-1" />

      <Button size="sm" variant="ghost" disabled={busy} onClick={() => void rescan(projectId)}>
        <RefreshCw className={cn('h-3 w-3', busy && 'animate-spin')} strokeWidth={1.75} /> Rescan
      </Button>

      {confirming ? (
        <span className="flex items-center gap-1 text-xs">
          <span className="text-[var(--fg-muted)]">Unlink? Files stay.</span>
          <Button size="sm" variant="danger" onClick={() => { setConfirming(false); void unlinkProject(projectId) }}>
            Unlink
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setConfirming(false)}>Cancel</Button>
        </span>
      ) : (
        <Tooltip label="Forget this folder. Nothing on disk is touched.">
          <Button size="sm" variant="ghost" onClick={() => setConfirming(true)}>
            <Unlink className="h-3 w-3" strokeWidth={1.75} /> Unlink
          </Button>
        </Tooltip>
      )}
    </header>
  )
}

/* ------------------------------------------------------------------ */
/* Doing it                                                            */
/* ------------------------------------------------------------------ */

/** Pull, Push and Sync, with what each of them would actually move. */
function Actions({ plan, counts, undecided, busy, onRun }: {
  plan: SyncPlan
  counts: { transfers: number; bytes: number; records: number }
  /** Conflicts nobody has answered yet — the only thing that blocks a clean finish. */
  undecided: number
  busy: boolean
  onRun: (kind: 'pull' | 'push' | 'sync') => void
}) {
  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-[var(--border)] bg-subtle px-3 py-2">
      <Tooltip label="Bring in every incoming change. Leaves your own work and every conflict alone.">
        <Button variant="secondary" disabled={busy || plan.incoming === 0} onClick={() => onRun('pull')}>
          <ArrowDown className="h-3.5 w-3.5" strokeWidth={1.75} />
          Pull<span className="tabular text-[var(--fg-subtle)]">{plan.incoming}</span>
        </Button>
      </Tooltip>

      <Tooltip label="Send every outgoing change. Never touches a file somebody else has locked.">
        <Button variant="secondary" disabled={busy || plan.outgoing === 0} onClick={() => onRun('push')}>
          <ArrowUp className="h-3.5 w-3.5" strokeWidth={1.75} />
          Push<span className="tabular text-[var(--fg-subtle)]">{plan.outgoing}</span>
        </Button>
      </Tooltip>

      <Tooltip label="Carry out every choice on this screen.">
        <Button variant="accent" disabled={busy || counts.transfers + counts.records === 0} onClick={() => onRun('sync')}>
          <FolderSync className="h-3.5 w-3.5" strokeWidth={1.75} />
          Sync<span className="tabular opacity-80">{counts.transfers}</span>
        </Button>
      </Tooltip>

      <span className="text-xs text-[var(--fg-subtle)]">
        {counts.transfers > 0 ? `${formatBytes(counts.bytes)} to move` : 'nothing chosen yet'}
        {counts.records > 0 && ` · ${counts.records} already match`}
        {undecided > 0 && (
          <span className="text-[var(--modified)]"> · {pluralise(undecided, 'conflict')} waiting on you</span>
        )}
      </span>
    </div>
  )
}

/**
 * Real numbers while it works, and a way out.
 *
 * Files and bytes both, because 900 drawings and one 300 MB assembly are the
 * same "1 of 1" and wildly different waits.
 */
function ProgressStrip({ progress }: { progress: Progress }) {
  const percent = progress.bytesTotal > 0
    ? Math.min(100, Math.round((progress.bytesDone / progress.bytesTotal) * 100))
    : progress.total > 0
      ? Math.min(100, Math.round((progress.done / progress.total) * 100))
      : 0

  const label = progress.phase === 'scanning' ? 'Scanning your folder'
    : progress.phase === 'applying' ? 'Syncing'
      : 'Choosing a folder'

  return (
    <div className="shrink-0 border-b border-[var(--border)] px-3 py-1.5">
      <div className="flex items-center gap-2 text-xs">
        <Spinner size={12} className="text-[var(--accent)]" />
        <span className="font-medium">{label}</span>
        <span className="tabular text-[var(--fg-subtle)]">
          {progress.total > 0 ? `${progress.done}/${progress.total} files` : 'counting…'}
          {progress.bytesTotal > 0 && ` · ${formatBytes(progress.bytesDone)} of ${formatBytes(progress.bytesTotal)}`}
        </span>
        <span className="min-w-0 flex-1 truncate text-[var(--fg-subtle)]">{progress.path}</span>
        <Button size="sm" variant="ghost" onClick={cancelRun}>
          <X className="h-3 w-3" strokeWidth={2} /> Cancel
        </Button>
      </div>
      <div
        className="mt-1 h-0.5 overflow-hidden rounded bg-[var(--bg-inset)]"
        role="progressbar"
        aria-valuenow={percent}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={label}
      >
        <div className="h-full bg-[var(--accent)] transition-[width] duration-200" style={{ width: `${percent}%` }} />
      </div>
    </div>
  )
}

/** What the last run did. Failures are named, never rolled into a count. */
function Summary({ report }: { report: RunReport }) {
  return (
    <div className="shrink-0 border-b border-[var(--border)] bg-subtle px-3 py-1.5 text-xs">
      <span className="font-medium">
        {report.cancelled ? 'Stopped' : 'Done'} · {pluralise(report.applied, 'file')} {report.kind === 'pull' ? 'pulled' : report.kind === 'push' ? 'pushed' : 'synced'}
      </span>
      <span className="text-[var(--fg-subtle)]"> · {formatBytes(report.bytes)}</span>
      {report.failures.length > 0 && (
        <span className="text-[var(--deleted)]">
          {' '}· {pluralise(report.failures.length, 'file')} failed: {report.failures.slice(0, 2).map(f => f.path).join(', ')}
          {report.failures.length > 2 && ` and ${report.failures.length - 2} more`}
        </span>
      )}
    </div>
  )
}

/**
 * Files the scan could not read, and what that costs.
 *
 * WHY THIS IS NOT A FOOTNOTE
 *   A file the shell could not open — a part open in SolidWorks, a folder a
 *   backup tool locked — is simply ABSENT from the scan, and an absent file
 *   looks exactly like a deleted one. The plan will therefore offer to delete
 *   it from Lattice for everybody. The store refuses to carry that out while
 *   any file is unread (`screenOperations`), and this says so before the
 *   person presses anything.
 */
function Unread({ count }: { count: number }) {
  return (
    <div className="flex shrink-0 items-center gap-2 border-b border-[var(--border)] bg-subtle px-3 py-1.5 text-xs">
      <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-[var(--modified)]" strokeWidth={1.75} />
      <span className="min-w-0 flex-1 text-[var(--fg-muted)]">
        <span className="font-medium text-[var(--fg)]">{pluralise(count, 'file')} could not be read</span>
        {' '}— probably open in another application. They are missing from this comparison, so nothing
        will be deleted from Lattice until a scan reads everything.
      </span>
    </div>
  )
}

/** Something went wrong, in words rather than a stack trace. */
function Problem({ message }: { message: string }) {
  return (
    <div className="flex shrink-0 items-center gap-2 border-b border-[var(--border)] bg-subtle px-3 py-1.5 text-xs text-[var(--deleted)]">
      <AlertTriangle className="h-3.5 w-3.5 shrink-0" strokeWidth={1.75} />
      <span className="min-w-0 flex-1 truncate">{message}</span>
      <Button size="sm" variant="ghost" onClick={() => useSyncFolder.getState().setError(undefined)}>Dismiss</Button>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* Groups and rows                                                     */
/* ------------------------------------------------------------------ */

interface GroupAction {
  label: string
  /** What it does, spelled out — a bulk button is the one that needs it most. */
  hint: string
  onClick: () => void
}

/** One titled band of rows, with the bulk choices that belong to it. */
function Group({ id, icon: Icon, tone, title, hint, items, overrides, onChoose, actions, projectId }: {
  id: string
  icon: typeof ArrowDown
  tone: string
  title: string
  hint: string
  items: readonly SyncItem[]
  overrides: ReadonlyMap<Path, Resolution>
  onChoose: (path: Path, resolution: Resolution) => void
  actions: readonly GroupAction[]
  projectId: ProjectId
}) {
  const headingId = `sync-group-${id}`
  const [showAll, setShowAll] = useState(false)
  // A first link of this team's project is ~900 incoming rows. Drawing them
  // all costs a second of layout for a list nobody reads past the top of, so
  // the tail is one button away. The GROUP ACTIONS still apply to every item,
  // shown or not — "accept all" would be a lie otherwise.
  const shown = showAll ? items : items.slice(0, MAX_ROWS)

  return (
    <section className="border-b border-[var(--border)] last:border-b-0">
      <header className="sticky top-0 z-10 flex flex-wrap items-center gap-2 border-b border-[var(--border)] bg-app px-3 py-1.5">
        <h3 id={headingId} className="flex items-center gap-1.5 text-sm font-medium">
          <Icon className={cn('h-3.5 w-3.5', tone)} strokeWidth={2} />
          {title}
          <span className="tabular text-xs text-[var(--fg-subtle)]">{items.length}</span>
        </h3>
        <p className="min-w-0 flex-1 truncate text-xs text-[var(--fg-subtle)]">{hint}</p>
        {actions.map(action => (
          <Button key={action.label} size="sm" variant="ghost" title={action.hint} onClick={action.onClick}>
            {action.label}
          </Button>
        ))}
      </header>

      <ul aria-labelledby={headingId}>
        {shown.map(item => (
          <ItemRow
            key={item.path}
            item={item}
            projectId={projectId}
            resolution={resolutionOf(item, overrides)}
            decided={overrides.has(item.path)}
            onChoose={onChoose}
          />
        ))}
        {shown.length < items.length && (
          <li className="px-3 py-2">
            <Button size="sm" variant="ghost" onClick={() => setShowAll(true)}>
              Show the other {pluralise(items.length - shown.length, 'file')}
            </Button>
          </li>
        )}
      </ul>
    </section>
  )
}

/** How many rows a group draws before it offers the rest behind a button. */
const MAX_ROWS = 150

/** The three answers to "what should happen to this file". */
const CHOICES: readonly { value: Resolution; label: string }[] = [
  { value: 'accept-remote', label: 'Accept' },
  { value: 'keep-local', label: 'Keep local' },
  { value: 'skip', label: 'Skip' },
]

/**
 * One file, what happened to it, and what to do about it.
 *
 * Memoised because choosing for ONE file must not re-render the other 900: the
 * props of every other row are identical between those two renders, and this
 * is the difference between a list that answers a click and one that stutters.
 */
const ItemRow = memo(function ItemRow({ item, projectId, resolution, decided, onChoose }: {
  item: SyncItem
  projectId: ProjectId
  resolution: Resolution
  decided: boolean
  onChoose: (path: Path, resolution: Resolution) => void
}) {
  // Selectors rather than the whole store: a plan can be 900 rows, and none
  // of them should re-render because somebody typed in the tree filter.
  const go = useUi(state => state.go)
  const open = useUi(state => state.open)
  const conflict = item.state === 'conflict'
  const needsChoice = conflict && !decided
  // The size of the side the row is about — and for a deletion, the size of
  // the copy that would go, which is the number a person wants before agreeing.
  const size = item.state.startsWith('pull')
    ? item.remote?.size ?? item.local?.size
    : item.local?.size ?? item.remote?.size

  return (
    <li
      className={cn(
        'flex items-center gap-2.5 border-b border-[var(--border)] px-3 py-1.5 last:border-b-0 hover-row',
        needsChoice && 'border-l-2 border-l-[var(--modified)] bg-[color-mix(in_oklch,var(--modified)_7%,transparent)]',
      )}
    >
      <FileIcon path={item.path} />

      <span className="min-w-0 flex-1">
        {item.remote ? (
          <button
            onClick={() => { go({ projectId, view: 'files' }); open(item.path) }}
            className="snap block max-w-full truncate text-left text-sm font-medium hover:text-[var(--accent)]"
          >
            {basename(item.path)}
          </button>
        ) : (
          <span className="block truncate text-sm font-medium">{basename(item.path)}</span>
        )}
        <span className="block truncate text-[11px] text-[var(--fg-subtle)]">{item.path}</span>
      </span>

      <span className={cn('hidden shrink-0 text-xs md:inline', conflict ? 'text-[var(--modified)]' : 'text-[var(--fg-muted)]')}>
        {describeItem(item)}
      </span>

      <span className="tabular w-16 shrink-0 text-right text-xs text-[var(--fg-subtle)]">
        {size === undefined ? '—' : formatBytes(size)}
      </span>

      {needsChoice && <Badge tone="modified">choose</Badge>}

      <ChoiceGroup item={item} value={resolution} decided={decided} onChoose={onChoose} />
    </li>
  )
})

/**
 * A three-way choice, as a real radio group.
 *
 * Radio semantics rather than three buttons: a screen reader then announces
 * "Accept, selected, 1 of 3" instead of three unrelated controls, and the
 * arrow keys move between them, which is what anybody who navigates by
 * keyboard will try first. One tab stop per row keeps a 200-row list crossable.
 */
function ChoiceGroup({ item, value, decided, onChoose }: {
  item: SyncItem
  value: Resolution
  decided: boolean
  onChoose: (path: Path, resolution: Resolution) => void
}) {
  const container = useRef<HTMLDivElement>(null)

  const step = (delta: number): void => {
    const index = CHOICES.findIndex(choice => choice.value === value)
    const next = CHOICES[(index + delta + CHOICES.length) % CHOICES.length]
    onChoose(item.path, next.value)
    const buttons = container.current?.querySelectorAll<HTMLButtonElement>('[role="radio"]')
    buttons?.[CHOICES.indexOf(next)]?.focus()
  }

  return (
    <div
      ref={container}
      role="radiogroup"
      aria-label={`What to do with ${basename(item.path)}`}
      onKeyDown={event => {
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') { event.preventDefault(); step(1) }
        if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') { event.preventDefault(); step(-1) }
      }}
      className="flex shrink-0 overflow-hidden rounded-[var(--radius-sm)] border border-[var(--border)]"
    >
      {CHOICES.map(choice => {
        const selected = choice.value === value
        // An unchosen conflict must not LOOK decided, so its default "skip"
        // is drawn as no selection at all rather than as a chosen answer.
        const shown = selected && (decided || item.state !== 'conflict')
        return (
          <button
            key={choice.value}
            type="button"
            role="radio"
            aria-checked={selected}
            tabIndex={selected ? 0 : -1}
            // A plain title rather than the app's Tooltip: three rich tooltip
            // roots per row is thousands of them in a big plan, and this text
            // is a description of a control that already reads clearly.
            title={describeChoice(item, choice.value)}
            onClick={() => onChoose(item.path, choice.value)}
            className={cn(
              'snap h-6 px-2 text-[11px] font-medium',
              shown
                ? 'bg-[var(--fg)] text-[var(--bg)]'
                : 'text-[var(--fg-muted)] hover:bg-[var(--bg-hover)] hover:text-[var(--fg)]',
            )}
          >
            {choice.label}
          </button>
        )
      })}
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* Blocked by a lock                                                   */
/* ------------------------------------------------------------------ */

/**
 * Files a colleague has claimed.
 *
 * There is no choice to offer here: we will not push over somebody's lock, and
 * taking it from them would make a lock worth nothing. The only useful action
 * is to ask, which writes a comment that @-mentions them on the file — one
 * mechanism, and the conversation stays where the work is.
 */
function BlockedGroup({ items, projectId }: { items: readonly SyncItem[]; projectId: ProjectId }) {
  return (
    <section className="border-b border-[var(--border)] last:border-b-0">
      <header className="sticky top-0 z-10 flex items-center gap-2 border-b border-[var(--border)] bg-app px-3 py-1.5">
        <h3 id="sync-group-blocked" className="flex items-center gap-1.5 text-sm font-medium">
          <Lock className="h-3.5 w-3.5 text-[var(--fg-subtle)]" strokeWidth={2} />
          Waiting on a lock
          <span className="tabular text-xs text-[var(--fg-subtle)]">{items.length}</span>
        </h3>
        <p className="min-w-0 flex-1 truncate text-xs text-[var(--fg-subtle)]">
          Your changes to these stay on this machine until the lock is released.
        </p>
      </header>
      <ul aria-labelledby="sync-group-blocked">
        {items.map(item => <BlockedRow key={item.path} item={item} projectId={projectId} />)}
      </ul>
    </section>
  )
}

function BlockedRow({ item, projectId }: { item: SyncItem; projectId: ProjectId }) {
  const lockLog = useWorkspace(state => state.locks)
  const roster = useWorkspace(state => state.roster)
  const locks = useMemo(() => locksHeldNow(lockLog), [lockLog])
  const lock = blockingLock(locks, projectId, item.path)
  // The plan names the holder; the lock log is what carries the id we need in
  // order to ask for it back. Either can be missing, so neither is assumed.
  const holderId = lock?.userId ?? item.lockedBy
  const holder = holderId ? personOf(roster, holderId) : undefined

  const [asking, setAsking] = useState(false)
  const [asked, setAsked] = useState(false)

  return (
    <li className="flex items-center gap-2.5 border-b border-[var(--border)] px-3 py-1.5 last:border-b-0 hover-row">
      <FileIcon path={item.path} />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium">{basename(item.path)}</span>
        <span className="block truncate text-[11px] text-[var(--fg-subtle)]">{item.path}</span>
      </span>

      <span className="flex shrink-0 items-center gap-1.5 text-xs text-[var(--fg-muted)]">
        {holder && <Avatar user={holder} size={16} />}
        {holder ? `${holder.name} locked this` : 'locked'}
        {lock && (
          <Tooltip label={formatExact(lock.at)}>
            <span className="cursor-help underline decoration-dotted underline-offset-2">{formatRelative(lock.at)}</span>
          </Tooltip>
        )}
        {lock?.note && <span className="hidden text-[var(--fg-subtle)] lg:inline">— {lock.note}</span>}
      </span>

      {lock && (
        <Tooltip label={`Ask ${(holder?.name ?? lock.userId).split(' ')[0]} to release it — writes a comment that mentions them`}>
          <Button
            size="sm" variant="ghost" disabled={asking || asked}
            onClick={() => {
              setAsking(true)
              void api.requestUnlock(lock.id)
                .then(() => setAsked(true))
                .catch(() => undefined)
                .finally(() => setAsking(false))
            }}
          >
            {asking ? <Spinner size={12} /> : <Hand className="h-3 w-3" strokeWidth={1.75} />}
            {asked ? 'Asked' : 'Request unlock'}
          </Button>
        </Tooltip>
      )}
    </li>
  )
}

